import React, { forwardRef } from 'react';
import { ExclamationCircleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

interface ValidatedInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  errors?: string[];
  isValid?: boolean;
  showValidIcon?: boolean;
  helpText?: string;
  containerClassName?: string;
}

/**
 * Composant d'input avec validation visuelle intégrée
 */
export const ValidatedInput = forwardRef<HTMLInputElement, ValidatedInputProps>(
  (
    {
      label,
      errors = [],
      isValid = false,
      showValidIcon = false,
      helpText,
      containerClassName = '',
      className = '',
      ...props
    },
    ref
  ) => {
    const hasErrors = errors.length > 0;

    // Classes dynamiques basées sur l'état de validation (design tokens)
    const inputClasses = `
      w-full px-token-sm py-token-xs border rounded-token-sm text-sm bg-surface
      focus:outline-none focus:ring-2 focus:ring-token-focus focus:ring-offset-token-focus transition-all duration-200
      ${
        hasErrors
          ? 'border-token-danger bg-token-danger-bg'
          : isValid && showValidIcon
            ? 'border-token-success bg-token-success-bg'
            : 'border-default'
      }
      ${className}
    `.trim();

    const labelClasses = `
      block text-sm font-medium mb-token-sm transition-colors duration-200
      ${hasErrors ? 'text-token-danger' : 'text-token-neutral'}
    `.trim();

    return (
      <div className={`space-y-token-xs ${containerClassName}`}>
        {/* Label */}
        <label className={labelClasses}>
          {label}
          {props.required && <span className="text-token-danger ml-token-xs">*</span>}
        </label>

        {/* Input avec icône de validation */}
        <div className="relative">
          <input ref={ref} className={inputClasses} {...props} />

          {/* Icône de validation */}
          {(hasErrors || (isValid && showValidIcon)) && (
            <div className="absolute inset-y-0 right-0 pr-token-sm flex items-center pointer-events-none">
              {hasErrors ? (
                <ExclamationCircleIcon className="h-5 w-5 text-token-danger" />
              ) : (
                <CheckCircleIcon className="h-5 w-5 text-token-success" />
              )}
            </div>
          )}
        </div>

        {/* Messages d'erreur */}
        {hasErrors && (
          <div className="space-y-token-xs">
            {errors.map((error, index) => (
              <p key={index} className="text-sm text-token-danger flex items-center">
                <ExclamationCircleIcon className="h-4 w-4 mr-token-xs flex-shrink-0" />
                {error}
              </p>
            ))}
          </div>
        )}

        {/* Texte d'aide */}
        {helpText && !hasErrors && <p className="text-sm text-muted">{helpText}</p>}
      </div>
    );
  }
);

ValidatedInput.displayName = 'ValidatedInput';
